import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {ExpressCheckoutSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useCheckoutRoute, useHistory} from '@aboutyou/router';
import {useIsDataInfoMonitoringConsentGiven} from '@aboutyou/storage';
import {useCOTracking, useTrackFeature} from '@aboutyou/tracking';
import {
  ContentPopper,
  FONT_SIZES,
  FONT_WEIGHTS,
  Flyout,
  ModalDialog,
  PrimaryContentPopperButton,
  SPACING,
  useShowToast,
} from '@aboutyou/ui-components';
import {useCustomerBiData} from '@aboutyou/user';
import {sessionStorageGetParsedItem} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAutoSignInWithSSO} from '../../../features/Authentication/AuthenticationModal/components/SSOButtonsFetcher/useAutoSignInWithSSO';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {useExpressCheckoutFlowLogger} from '../../../features/Authentication/hooks/useExpressCheckoutFlowLogger';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';
import {CHECKOUT_URL_PARAMS} from '../../../features/Checkout/constants';
import {useGoToCheckoutCallback} from '../../../features/Checkout/useGoToCheckoutCallback';
import {ScayleCheckoutScript} from '../../Checkout/components/ScayleLoadingScript';
import {
  ExpressCheckoutElement,
  ExpressCheckoutErrorEvent,
  ExpressCheckoutFinishedEvent,
  InvalidAccessTokenEvent,
  RawExpressCheckoutComponent,
} from './RawExpressCheckoutComponent';
import {ExpressPaymentKey, PaymentProviders} from './enums';

export const ExpressCheckoutView = (
  props: ExpressCheckoutSection & {
    basketTotal?: number;
  },
) => {
  const history = useHistory();
  const checkoutRoute = useCheckoutRoute();
  const {track} = useCOTracking();
  const {Log} = useExpressCheckoutFlowLogger();
  const {pc180Score} = useCustomerBiData();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const {isAuthenticated} = useIsAuthenticated();
  const showToast = useShowToast();
  const {t} = useTranslation();
  const hasDatadogConsent = useIsDataInfoMonitoringConsentGiven();
  const goToCheckoutCallback = useGoToCheckoutCallback();
  const isComponentInteractedWith = useRef(false);
  const expressPaymentKey =
    props.type?.$case === 'klarnaExpress'
      ? ExpressPaymentKey.klarna
      : ExpressPaymentKey.paypal;
  const {isActive: isKlarnaActive, activateAbTest} =
    useIsFeatureActive('ab_klarna_express');
  const trackFeature = useTrackFeature();
  const [openKlarnaNavigateModal, setOpenKlarnaNavigateModal] = useState(false);
  const clickedPaymentProvider = useRef<
    {paymentKey: string; provider: string} | undefined
  >(undefined);
  const webComponent = useRef<ExpressCheckoutElement | null>(null);

  const getInvalidAccessToken = () => {
    // this is hacky way to trigger validation of access-token
    // If we pass "invalid-token" string, web component will treat it as guest session and no validation will be triggered
    return 'invalid-token'.repeat(60);
  };

  const memoData = useMemo(() => {
    switch (props.type?.$case) {
      case 'klarnaExpress':
        return {
          jwt: props.type.klarnaExpress.jwt,
          accessToken:
            isAuthenticated && props.type.klarnaExpress.coAccessToken === ''
              ? getInvalidAccessToken()
              : props.type.klarnaExpress.coAccessToken,
          type: PaymentProviders.Klarna,
        };
      case 'paypalExpress':
        return {
          jwt: props.type.paypalExpress.jwt,
          accessToken:
            isAuthenticated && props.type.paypalExpress.coAccessToken === ''
              ? getInvalidAccessToken()
              : props.type.paypalExpress.coAccessToken,
          type: PaymentProviders.Paypal,
        };
      default:
        throw new Error('[checkout-express] unsupported payment provider');
    }
  }, [isAuthenticated, props]);

  const navigateToCheckout = useCallback(
    (searchParamsProp?: URLSearchParams) => {
      const searchParams =
        searchParamsProp || new URLSearchParams(document.location.search);
      history.push({
        pathname: checkoutRoute,
        search: `?${searchParams.toString()}`,
      });
    },
    [checkoutRoute, history],
  );

  const handleCheckoutFinished = useCallback(
    ({detail}: ExpressCheckoutFinishedEvent) => {
      const searchParams = new URLSearchParams(document.location.search);
      searchParams.set(CHECKOUT_URL_PARAMS.transactionId, detail?.id);
      searchParams.set(CHECKOUT_URL_PARAMS.paymentProvider, expressPaymentKey);
      searchParams.set(
        CHECKOUT_URL_PARAMS.entryTimeStamp,
        Date.now().toString(),
      );
      setOpenKlarnaNavigateModal(false);
      track({
        type: 'ExpressCheckoutAuthorization',
        pc180Score,
        expressPaymentKey,
        review: !detail.isFinalized, // True if the customer is delegated to the review page; False if the customer goes directly to OSP.
      });

      if (!detail.isFinalized) {
        navigateToCheckout(searchParams);
      } else {
        console.log('[express checkout] navigate to OSP');
      }
    },
    [expressPaymentKey, track, pc180Score, navigateToCheckout],
  );

  /**
   * When this event triggers: in situations where user is logged in and access-token is empty string
   * That means that user session is expired and we need to refresh it.
   *
   * We dont need to handle onSuccess or onCancel here as if user refreshes session getBasketPageBulk request would get trigger again and new access-token would be fetched
   * OnCancel we dont need to do anything as user activily declined to do action and we just stay on basket as it is.
   *
   * NOTE: dont confuse this with guest session, in that case too access-token is empty string but we dont need to refresh it.
   * diff is that user is not logged in
   */
  const handleInvalidAccessToken = useCallback(
    ({detail}: InvalidAccessTokenEvent) => {
      clickedPaymentProvider.current = detail;
      showUserAuthenticationPrompt({
        context: 'basket-page-express-checkout-invalid-access-token',
        forceSessionRefresh: true,
        needsCheckoutSession: true,
        onSuccess: () => {
          setOpenKlarnaNavigateModal(true);
        },
      });
    },
    [showUserAuthenticationPrompt],
  );

  const handlePaymentCancelled = useCallback(() => {
    track({
      type: 'ExpressCheckoutCancel',
      pc180Score,
      expressPaymentKey,
    });
    setOpenKlarnaNavigateModal(false);
  }, [track, pc180Score, expressPaymentKey]);

  const handleExpressError = useCallback(
    ({detail}: ExpressCheckoutErrorEvent) => {
      track({
        type: 'ExpressCheckoutWidgetError',
        pc180Score,
        expressPaymentKey,
        errorReason: typeof detail === 'string' ? detail : detail.message,
        statusCode: typeof detail === 'string' ? 0 : detail.statusCode,
        errorKey: typeof detail === 'string' ? detail : detail.errorKey,
      });

      if (isComponentInteractedWith.current) {
        // 403 and 401 status code are not considered as an error
        // they are invalid checkout session stuff to be handled
        if (
          typeof detail !== 'string' &&
          detail.statusCode !== 403 &&
          detail.statusCode !== 401
        ) {
          setOpenKlarnaNavigateModal(false);
        }
        showToast({
          type: 'ERROR',
          content: t(
            'Oops! An error has occurred with the {provider} functionality. Please use the regular checkout',
            {
              provider:
                props.type?.$case === 'klarnaExpress'
                  ? 'Klarna Express'
                  : 'PayPal Express',
            },
          ),
        });
        showUserAuthenticationPrompt({
          context: 'basket-page-express-checkout-error',
          needsCheckoutSession: true,
          forceSessionRefresh: true,
          forceRedirectToCheckoutOnSSOLoginSuccess: true,
          onSuccess: () => {
            const customDataJWTPart = memoData.jwt.split('.')[1];
            if (customDataJWTPart) {
              const customData = JSON.parse(atob(customDataJWTPart));
              if (customData?.express) {
                navigateToCheckout();
                return;
              }
            }

            goToCheckoutCallback();
          },
        });
      }
    },
    [
      expressPaymentKey,
      goToCheckoutCallback,
      memoData.jwt,
      navigateToCheckout,
      pc180Score,
      props.type?.$case,
      showToast,
      showUserAuthenticationPrompt,
      t,
      track,
    ],
  );

  const handlePaymentStart = useCallback(() => {
    isComponentInteractedWith.current = true;
    trackFeature({
      eventType: 'click',
      label: `${
        props.type?.$case === 'klarnaExpress' ? 'klarna' : 'paypal'
      }_express_checkout`,
      name: 'proceed_checkout',
    });

    track({
      type: 'ExpressCheckoutEntry',
      pc180Score,
      expressPaymentKey,
    });

    Log({
      event: 'exp-co-widget-entry',
      attributes: {},
    });
  }, [
    Log,
    expressPaymentKey,
    pc180Score,
    props.type?.$case,
    track,
    trackFeature,
  ]);

  useEffect(() => {
    activateAbTest(
      process.env.STACK === 'desktop'
        ? 'ab_klarna_express_d'
        : 'ab_klarna_express_m',
    );
  }, [activateAbTest]);

  const {signIn, RenderLoadingState} = useAutoSignInWithSSO();

  if (!isKlarnaActive) {
    return null;
  }

  const handleExpressButtonClick = (e: React.MouseEvent) => {
    Log({
      event: 'exp-co-click',
      attributes: {
        'logged-in': isAuthenticated,
      },
    });

    if (isAuthenticated) {
      return;
    }
    /**
     * Prevent the button click from propagating to Express Checkout web component since we want to force the user to login with Klarna before using the express checkout
     */
    e.stopPropagation();
    e.preventDefault();

    signIn({
      idp: 'klarna',
      onSuccess: async () => {
        /**
         * Try to get the payment methods from the Scayle Checkout configs
         */
        if (window.scayleCheckout?.express?.V3) {
          await window.scayleCheckout.express.V3.preload(process.env.STACK);

          const paymentMethods = sessionStorageGetParsedItem(
            'expressPaymentMethods',
          ) as undefined | [{provider: string; paymentKey: string}];

          if (paymentMethods?.[0]) {
            const paymentKey = paymentMethods[0].paymentKey;
            const provider = paymentMethods[0].provider;

            if (paymentKey && provider) {
              clickedPaymentProvider.current = {paymentKey, provider};
            }
          }
        }

        /**
         * fallback in case we dont have payment methods in the session storage
         */
        if (
          !clickedPaymentProvider.current?.paymentKey ||
          !clickedPaymentProvider.current?.provider
        ) {
          clickedPaymentProvider.current = {
            paymentKey: 'spay_klarna_paylater',
            provider: 'klarna',
          };
        }

        setOpenKlarnaNavigateModal(true);
      },
      onError: () => {
        showToast({
          type: 'ERROR',
          content: t(
            'Oops! An error has occurred with the {provider} functionality. Please use the regular checkout',
            {provider: 'Klarna Express'},
          ),
        });
      },
    });
  };

  return (
    <ExpressCheckoutWrapper>
      {RenderLoadingState}
      <ScayleCheckoutScript />
      <ShallowButtonWrapper onClickCapture={handleExpressButtonClick}>
        <RawExpressCheckoutComponent
          ref={webComponent}
          data-testid={`express-checkout-web-component-${memoData.type}`}
          device="web"
          jwt={memoData.jwt}
          accessToken={memoData.accessToken}
          basketTotal={props.basketTotal}
          onInvalidAccessToken={handleInvalidAccessToken}
          onExpressCheckoutFinished={handleCheckoutFinished}
          onPaymentClosed={handlePaymentCancelled}
          onExpressError={handleExpressError}
          onPaymentStart={handlePaymentStart}
          hasDatadogConsent={hasDatadogConsent}
        />
      </ShallowButtonWrapper>

      <ContentPopper
        LayoutComponent={process.env.STACK === 'mobile' ? Flyout : ModalDialog}
        open={openKlarnaNavigateModal}
        onClose={() => {
          Log({
            event: 'exp-co-popup-cancel',
            attributes: {},
          });
          setOpenKlarnaNavigateModal(false);
        }}
        renderInPortal
        bodyContent={
          <NavigateModalBody>
            <BodyTextContent>
              <TitleText>
                {t('You have been successfully logged-in.')}
              </TitleText>

              <SubTitleText>
                {t(
                  'Please click on the button below to proceed to payment with Klarna.',
                )}
              </SubTitleText>
            </BodyTextContent>
            <StyledContinueButton
              label={t('Continue with Klarna')}
              onClick={() => {
                setOpenKlarnaNavigateModal(false);
                if (webComponent.current) {
                  webComponent.current.setAttribute(
                    'access-token',
                    memoData.accessToken,
                  );

                  webComponent.current.setAttribute(
                    'express-direct-entry',
                    JSON.stringify({
                      paymentKey: clickedPaymentProvider.current?.paymentKey,
                      provider: clickedPaymentProvider.current?.provider,
                    }),
                  );

                  Log({
                    event: 'exp-co-popup-continue',
                    attributes: {},
                  });
                }
              }}
              data-testid="express-checkout-continue-to-checkout"
            />
          </NavigateModalBody>
        }
      />
    </ExpressCheckoutWrapper>
  );
};

const BodyTextContent = styled.div`
  display: flex;
  gap: ${SPACING['Y.Md']};
  flex-direction: column;

  & > p {
    margin: 0;
  }
`;

const StyledContinueButton = styled(PrimaryContentPopperButton)`
  width: 100%;
`;

const TitleText = styled.p`
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.w500};
  text-align: center;
`;

const SubTitleText = styled.p`
  font-size: ${FONT_SIZES.px14};
  text-align: center;
`;

const NavigateModalBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-direction: column;
  gap: ${SPACING['Y.Lg']};
`;

const ExpressCheckoutWrapper = styled.div`
  padding-top: 8px;
  min-height: 66px;
`;

const ShallowButtonWrapper = styled.div`
  height: 100%;
`;
